/**
 * Heading Properties Configuration
 *
 * Defines display properties for report heading metrics
 * Matches Angular's heading-properties.ts pattern
 */

import { formatNumber, formatCurrency, formatPercent } from '@/utils/formatters';
import type { ReportsHeading } from '@/types/poolReports.types';

export interface HeadingProperty {
  label: string;
  key: keyof ReportsHeading;
  format: (heading: ReportsHeading) => string;
  showInYearly: boolean;
  showInMonthly: boolean;
  showInMobile: boolean;
  hideForYTD?: boolean; // Hide for YTD headings
  hideForFullYear?: boolean; // Hide for Full Year headings
}

export const headingPropertiesConfig: HeadingProperty[] = [
  {
    label: 'Average Points',
    key: 'averagePoints',
    format: (h) => formatNumber(h.averagePoints, 2, 2),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: true,
  },
  {
    label: 'Average Earnings',
    key: 'averageEarnings',
    format: (h) => formatCurrency(h.averageEarnings, false, 0),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: true,
  },
  {
    label: '100 Points Earnings',
    key: 'oneHundredPointsEarnings',
    format: (h) => formatCurrency(h.oneHundredPointsEarnings, false, 0),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: true,
  },
  {
    label: 'Total Offhire',
    key: 'totalOffhire',
    format: (h) => formatNumber(h.totalOffhire, 0, 0),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: true,
  },
  {
    label: 'Percentage Offhire',
    key: 'percentOffhire',
    format: (h) => formatPercent(h.percentOffhire, 2),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: true,
  },
  {
    label: 'Average Fleet',
    key: 'averageFleet',
    format: (h) => formatNumber(h.averageFleet, 2, 2),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: true,
  },
  {
    label: 'Admin Onhire Day',
    key: 'adminOnHireDay',
    format: (h) => formatCurrency(h.adminOnHireDay, false, 0),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: false,
  },
  {
    label: 'Bunker Onhire Day',
    key: 'bunkerOnHireDay',
    format: (h) => formatCurrency(h.bunkerOnHireDay, false, 0),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: false,
  },
  {
    label: 'Average Vessel Distribution',
    key: 'averageVesselDistribution',
    format: (h) => formatCurrency(h.averageVesselDistribution, false, 0),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: true,
    hideForYTD: false, // Only hidden for Full Year, NOT for YTD
    hideForFullYear: true, // Hidden for Full Year headings in Angular
  },
  {
    label: '100 Points Distribution',
    key: 'oneHundredPointsDistribution',
    format: (h) => formatCurrency(h.oneHundredPointsDistribution, false, 0),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: true,
    hideForYTD: false, // Only hidden for Full Year, NOT for YTD
    hideForFullYear: true, // Hidden for Full Year headings in Angular
  },
  {
    label: 'Working Capital B/fwd',
    key: 'workingCapitalBFwd',
    format: (h) => formatCurrency(h.workingCapitalBFwd, false, 0),
    showInYearly: true,
    showInMonthly: true,
    showInMobile: true,
    hideForYTD: false, // Only hidden for Full Year, NOT for YTD
    hideForFullYear: true, // Hidden for Full Year headings in Angular
  },
];

/**
 * Get visible heading properties based on report type and heading type
 */
export function getVisibleHeadings(
  reportType: 'yearly' | 'monthly',
  isYTD: boolean,
  isFullYear: boolean,
  isMobile: boolean
): HeadingProperty[] {
  return headingPropertiesConfig.filter((prop) => {
    // Check report type visibility
    const reportTypeVisible = reportType === 'yearly' ? prop.showInYearly : prop.showInMonthly;
    if (!reportTypeVisible) return false;

    // Check mobile visibility
    if (isMobile && !prop.showInMobile) return false;

    // Check YTD visibility (rare case)
    if (isYTD && prop.hideForYTD) return false;

    // Check Full Year visibility (hides last 3 fields in Angular)
    if (isFullYear && prop.hideForFullYear) return false;

    return true;
  });
}

/**
 * Get count of visible headings for layout purposes
 */
export function getVisibleHeadingCount(
  reportType: 'yearly' | 'monthly',
  isYTD: boolean,
  isFullYear: boolean
): number {
  return getVisibleHeadings(reportType, isYTD, isFullYear, false).length;
}
